<template>
  <BaseModal v-model="value">
    <template #title>
      {{
        `${activityLabel('storeTask')}: ${timer.children[timer.children.length - 1].data.task_id}`
      }}
    </template>
    <template #content>
      <div class="row q-gutter-sm q-my-sm">
        <div class="col">
          <q-input
            ref="startRef"
            :model-value="
              useFormatDate(start).time({
                hour: 'numeric',
                minute: 'numeric',
                hourCycle: 'h23',
              })
            "
            readonly
            color="accent"
            filled
            type="time"
            :label="formLabel('startTime')"
          />
        </div>
      </div>
      <div class="row q-gutter-sm q-my-sm">
        <div class="col">
          <q-input
            ref="endRef"
            :model-value="
              useFormatDate(end).time({
                hour: 'numeric',
                minute: 'numeric',
                hourCycle: 'h23',
              })
            "
            @update:model-value="(val) => (isValidDate(val) ? updateEnd(toDate(val, start) as Date) : undefined)"
            :rules="[
              (val) => isValidDate(val) || '',
              (val) => rules.beforeNow(toDate(val, start) as Date) || formLabel('notBeforeNow'),
              (val) => rules.notBetweenTimers(toDate(val, start) as Date, parentIndex) || formLabel('betweenActivities'),
              (val) => childRules.newSumChildrenNotGreaterThanParent(
                { start_time: toDate(start, start) as Date, end_time: toDate(val, start) as Date }, parentIndex, timer.children.length - 1)
                || formLabel('childrenSum'),
              () =>
                useChildTimerRules().notBeforeStart({ childStart: start, childEnd: end }, timer) ||
                formLabel('endOverlapStart'),
            ]"
            color="accent"
            filled
            type="time"
            :label="formLabel('endTime')"
          />
        </div>
      </div>
    </template>
    <template #actions>
      <q-btn-dropdown
        color="primary"
        split
        @click="editActivity"
        :label="`${formLabel('save')} ${activityLabel('storeTask')}`"
      >
        <q-list>
          <q-item clickable v-close-popup="2">
            <q-item-section>
              <q-item-label>{{ formLabel('cancel') }}</q-item-label>
            </q-item-section>
          </q-item>
        </q-list>
      </q-btn-dropdown>
    </template>
  </BaseModal>
</template>
<script setup lang="ts">
import { Timer } from 'app/types/timers'
import { Ref, inject, onBeforeMount, ref } from 'vue'
import { isValidDate, toDate, stripTime } from '../utils'
import { useStartEndRules } from '../composables/startEndRules'
import { useChildTimerRules } from '../composables/useChildTimerRules'
import { useFormatDate } from '../composables/formatDate'
import { useI18n } from 'vue-i18n'
import BaseModal from './wrappers/BaseModal.vue'
import { useModelWrapper } from './wrappers/helpers/useModelWrapper'
import { useTimers } from '../store/timers'

const props = defineProps<{ timer: Timer; modelValue: boolean; parentIndex: number }>()
const emit = defineEmits<{
  (e: 'update:modelValue', data: any): void
}>()
const value = useModelWrapper(props, emit)

const date = inject('date', ref(new Date()))
const { t } = useI18n({ useScope: 'global' })
const formLabel = (key: string) => t(`form.${key}`)
const activityLabel = (key: string) => t(`activities.${key}`)

const start = ref<Date>(new Date())
stripTime(start, date.value)
const end = ref()
const startRef = ref()
const endRef = ref()

const rules = useStartEndRules({ start, end })
const childRules = useChildTimerRules()

onBeforeMount(() => {
  start.value = props.timer.children[props.timer.children.length - 1].start_time
  stripTime(start, date.value)
  end.value = props.timer.children[props.timer.children.length - 1].end_time ?? undefined
  if (end.value) {
    stripTime(end, date.value)
  }
})
const validate = () => {
  if (startRef.value) {
    startRef.value.validate()
    if (startRef.value.hasError) return false
  }
  if (endRef.value) {
    endRef.value.validate()
    if (endRef.value.hasError) return false
  }
  return true
}
const updateTimer =
  (timerRef: Ref<Date>) =>
  (date: Date): void => {
    timerRef.value = date
  }
updateTimer(start)
const updateEnd = updateTimer(end)

const { editChildStartEnd } = useTimers()
const editActivity = () => {
  if (validate()) {
    stripTime(start, date.value)
    stripTime(end, date.value)

    if (end.value) {
      editChildStartEnd(
        { start_time: start.value, end_time: end.value },
        props.parentIndex,
        props.timer.children.length - 1
      )
    }
    value.value = false
  }
}
</script>
